import styled from 'styled-components';
import {
  Map,
  useGetImageDimensions,
  getShapesFromResourceGroups,
} from '@kouto/map';
import { ShapeProps, ResourceGroupV3 } from '@kouto/types';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react';
import { Map as LeafletMap } from 'leaflet';
import debounce from 'lodash/debounce';
import useResourceGroupAttributeLabels from 'api/experience/useResourceGroupAttributeLabels';
import useResourceBookingSession from 'features/Reserve/hooks/useResourceBookingSession';
import { useTranslation } from 'react-i18next';
import { useResourceMapContext } from '../../features/Reserve/contexts/ResourceMapContext';

interface ResourceMapProps {
  assetUrl: string;
  resourceGroups: ResourceGroupV3[];
  collectionName?: string;
}

const ResourceMap = ({
  assetUrl,
  resourceGroups,
  collectionName,
}: ResourceMapProps) => {
  const mapRef = useRef<LeafletMap | null>(null);
  const { t } = useTranslation();
  const {
    focusedResources,
    selectResource,
    selectedResource,
    unselectResource,
  } = useResourceMapContext();
  const { groupId, setResourceBookingSessionParams } =
    useResourceBookingSession();
  // Pins show a resource's attributes on hover once its group is open.
  const openGroupExperienceIds = useMemo(
    () =>
      resourceGroups
        .find((group) => group.id === groupId)
        ?.experiences.map((experience) => experience.id) ?? [],
    [groupId, resourceGroups],
  );
  const attributeLabels = useResourceGroupAttributeLabels({
    experienceIds: openGroupExperienceIds,
    resourceGroupId: groupId,
  });
  const mapImageDimensions = useGetImageDimensions(assetUrl);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerRect, setContainerRect] = useState(
    containerRef.current?.getBoundingClientRect(),
  );

  const onResourceSelect = useCallback(
    (shape: ShapeProps) => {
      if (shape.id !== selectedResource) {
        selectResource(shape.id);
        if (!groupId) {
          const group = resourceGroups.find((g) =>
            g.experiences.some((e) => e.id === shape.id),
          );
          if (group) {
            setResourceBookingSessionParams({
              groupId: group.id,
              resourceId: shape.id,
            });
          }
        }
      } else {
        unselectResource();
      }
    },
    [
      groupId,
      resourceGroups,
      selectResource,
      selectedResource,
      setResourceBookingSessionParams,
      unselectResource,
    ],
  );

  const shapes = useMemo(
    () =>
      containerRect &&
      containerRect.width > 0 &&
      containerRect.height > 0 &&
      mapImageDimensions &&
      mapImageDimensions.width > 0 &&
      mapImageDimensions.height > 0
        ? getShapesFromResourceGroups(
            resourceGroups.map((rg) => ({
              ...rg,
              experiences: rg.experiences.map((exp) => ({
                ...exp,
                position: exp.position ?? null,
                number: exp.number ?? null,
                shape: exp.shape ?? null,
                title: exp.title ?? undefined,
                maximumAllowedToBook: exp.maximumAllowedToBook ?? null,
                hoverText: attributeLabels.get(exp.id),
              })),
            })),
            containerRect,
            mapImageDimensions,
            {
              focusedResources,
              selectedResource: selectedResource ?? undefined,
            },
          )
        : {},
    [
      attributeLabels,
      resourceGroups,
      focusedResources,
      selectedResource,
      containerRect,
      mapImageDimensions,
    ],
  );

  const [, startTransition] = useTransition();

  useEffect(() => {
    const updateDimensions = debounce(() => {
      startTransition(() => {
        setContainerRect(containerRef.current?.getBoundingClientRect());
      });
    }, 250);

    const resizeObserver = new ResizeObserver(updateDimensions);
    const containerElement = containerRef.current;
    if (containerElement) {
      setContainerRect(containerElement.getBoundingClientRect());
      resizeObserver.observe(containerElement);
    }

    return () => {
      if (containerElement) {
        resizeObserver.unobserve(containerElement);
      }
      resizeObserver.disconnect();
    };
  }, [containerRef]);

  return (
    <MapWrapper ref={containerRef}>
      {!mapImageDimensions?.width || !containerRect ? null : (
        <Map
          translate={t}
          height={containerRect.height}
          width={containerRect.width}
          key={`${containerRect.width}-${containerRect.height}`}
          mapRef={mapRef}
          shapes={shapes}
          onShapeClick={onResourceSelect}
          assetUrl={assetUrl}
          draggable={false}
          alt={t('interactiveMapAltText', {
            title: collectionName || resourceGroups[0]?.title,
          })}
        />
      )}
    </MapWrapper>
  );
};

export default ResourceMap;

const MapWrapper = styled.div`
  width: 100%;
  height: 100%;
`;
